'use client'
import { useCallback, useState, useEffect, useRef } from 'react';
import { CopilotFeedbackScreen } from './copilot-feedback-screen';
import type {
  ProcessingUpdateEvent,
  ToolExecutionEvent,
  BotErrorEvent,
  LongRunningOperationEvent
} from '@/types/signalr';
import { useBotEvents } from '@/hooks/use-bot-events';
import { ResizablePanel } from './resizable';
import { useMotionValue, useMotionValueEvent, motion, AnimatePresence } from 'framer-motion';
import { useCopilot } from '@/providers/copilot-provider';
import { Sparkles, AlertCircle, Wrench, Play, Bot } from "lucide-react";
import { Alert, AlertDescription } from '@/components/ui/alert';
import { useAuth } from '@/contexts/auth-context';
import { Button } from '@/components/ui/button';
import { Progress } from '@/components/ui/progress';
import { cn } from '@/lib/utils';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { useAIBot } from '@/hooks/use-ai-bot';
import { SessionType, BotMessage } from '@/types/ai-bot-framework';
import { useAIBotProvider } from '@/contexts/ai-bot-context';

interface Message {
  role: 'user' | 'assistant';
  content: string;
  timestamp?: string;
  id: string;
  status?: 'thinking' | 'streaming' | 'complete' | 'error';
  error?: {
    message: string;
    code: string;
    isUserFacing: boolean;
  };
  progress?: {
    stage: string;
    percent?: number;
  };
  toolExecutions?: Array<{
    name: string;
    status: 'Started' | 'InProgress' | 'Completed' | 'Failed';
    error?: string;
  }>;
  isMarkdown?: boolean;
  isActionResult?: boolean;
  metadata?: {
    isHiddenInit?: boolean;
    [key: string]: any;
  };
}

// Helper function to detect if content contains Markdown formatting
function containsMarkdown(content: string): boolean {
  if (!content || typeof content !== 'string') return false;

  // Look for common Markdown patterns
  const markdownPatterns = [
    /^#+\s.+$/m,                // Headers
    /^\s*[-*+]\s.+$/m,         // Unordered lists
    /^\s*\d+\.\s.+$/m,         // Ordered lists
    /^\s*>\s.+$/m,             // Blockquotes
    /^\s*```[\s\S]*?```\s*$/m, // Code blocks
    /\|.+\|.+\|/m,             // Tables
    /^\s*---+\s*$/m,           // Horizontal rules
    /\[.+?\]\(.+?\)/          // Links
  ];

  // Special case for tables - more robust detection
  if (content.includes('|') && /\|[^|]+\|[^|]+\|/.test(content)) {
    return true;
  }

  // Special case for markdown headers
  if (content.includes('##') || /^#\s.+$/m.test(content)) {
    return true;
  }

  return markdownPatterns.some(pattern => pattern.test(content));
}

// AnimatedDots component for the thinking indicator
function AnimatedDots({ content, toolCallCount, currentToolCalls }: {
  content?: string,
  toolCallCount?: number,
  currentToolCalls?: string[]
}) {
  const [dotCount, setDotCount] = useState(1);
  useEffect(() => {
    const interval = setInterval(() => setDotCount(d => (d % 3) + 1), 500);
    return () => clearInterval(interval);
  }, []);

  // Use the content if available, otherwise default to "Thinking"
  let displayText = content && content.trim() ? content : "Thinking";

  // Add tool call information if available
  if (toolCallCount && toolCallCount > 0) {
    const latestTool = currentToolCalls?.[currentToolCalls.length - 1];
    displayText = `Running ${latestTool || 'tool'} (${toolCallCount} tool${toolCallCount > 1 ? 's' : ''} called)`;
  }

  return (
    <span
      style={{
        marginLeft: 8,
        background: 'rgba(128,128,128,0.15)',
        color: '#666',
        borderRadius: 8,
        padding: '3px 10px',
        fontSize: '0.9em',
        fontStyle: 'italic',
        fontFamily: 'inherit',
        verticalAlign: 'middle',
        display: 'inline-block',
        marginTop: 4,
      }}
    >
      {displayText}{'.'.repeat(dotCount)}
    </span>
  );
}

function formatDuration(ms: number) {
  const s = Math.round(ms / 1000);
  if (s < 60) return `${s} second${s !== 1 ? 's' : ''}`;
  const m = Math.floor(s / 60);
  return `${m} minute${m !== 1 ? 's' : ''}`;
}

import { useFeedback } from '@/hooks/use-feedback';

interface ActionButton {
  actionId: string;
  label: string;
  icon: string;
  variant: string;
  actionMetadata: Record<string, unknown>;
}

export function CopilotChat() {
  // Copilot mode: 'chat' | 'feedback' (from context)
  const { isVisible, copilotMode, setCopilotMode } = useCopilot();
  const { context } = useFeedback();
  const auth = useAuth();
  const [messages, setMessages] = useState<Message[]>([]);
  const [_errors, setErrors] = useState<Map<string, BotErrorEvent>>(new Map());
  const [input, setInput] = useState('');
  const [autoSubmit, setAutoSubmit] = useState(false);
  const [isBotSelectorOpen, setIsBotSelectorOpen] = useState(false);
  const width = useMotionValue(400);
  const formRef = useRef<HTMLFormElement>(null);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const [thinkingDuration, _setThinkingDuration] = useState<number | null>(null);
  const [lastCompletedThinkingId, _setLastCompletedThinkingId] = useState<string | null>(null);
  const [toolCallCount, setToolCallCount] = useState<number>(0);
  const [currentToolCalls, setCurrentToolCalls] = useState<string[]>([]);
  const [thinkingMessageId, setThinkingMessageId] = useState<string | null>(null);
  const [actionButtons, setActionButtons] = useState<ActionButton[]>([]);
  const [executingActions, setExecutingActions] = useState<Set<string>>(new Set());

  const generateMessageId = useCallback(() => {
    return `${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
  }, []);

  // Get SignalR connection
  const { connection, registerMessageHandler, unregisterMessageHandler } = useAIBotProvider();

  // Use the modern AI Bot Framework
  // Note: pageContext is now fetched fresh from URL on every message send
  const {
    sessionId,
    isConnected,
    isRegistered,
    currentBot,
    availableBots,
    isProcessing,
    error: _botError,
    sendMessage: sendBotMessage,
    switchBot,
    clearMessages: _clearBotMessages
  } = useAIBot({
    requestedBotId: 'qa-form-builder',
    sessionType: SessionType.Visible,
    onMessage: useCallback((message: BotMessage) => {
      // 🚨 Check for ActionButton FIRST
      if (message.metadata?.actionButton) {
        console.log('[Copilot] ✅ ActionButton detected:', message.metadata.actionButton);
        const button = message.metadata.actionButton as ActionButton;
        setActionButtons(prev => {
          if (prev.some(b => b.actionId === button.actionId)) return prev;
          return [...prev, button];
        });
      }

      // Handle incoming bot messages
      const messageId = message.requestId;
      const isStatusUpdate = !message.content && message.metadata && message.metadata.status;

      // Don't treat completion/system messages as thinking triggers
      const isCompletionMessage = message.isComplete || message.messageType === 'System';

      // Parse tool calls from content
      const toolCallMatch = message.content?.match(/\[Calling tool: (.+?)\]/);
      if (toolCallMatch) {
        const toolName = toolCallMatch[1];
        setCurrentToolCalls(prev => [...prev, toolName]);
        setToolCallCount(prev => prev + 1);
      }

      setMessages(prev => {
        const existingIndex = prev.findIndex(msg => msg.id === messageId);
        if (existingIndex === -1) {
          // Create new assistant message
          const newContent = message.content || '';
          const isMarkdown = containsMarkdown(newContent);

          const newMessage: Message = {
            id: messageId,
            role: 'assistant',
            content: newContent,
            timestamp: new Date(message.timestamp).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }),
            status: (isStatusUpdate && !isCompletionMessage) ? 'thinking' : 'streaming',
            isMarkdown: isMarkdown
          };

          if (isStatusUpdate && !isCompletionMessage) {
            setThinkingMessageId(messageId);
          }

          return [...prev, newMessage];
        }

        return prev.map(msg => {
          if (msg.id === messageId) {
            let newStatus: 'thinking' | 'streaming' | 'complete' | 'error';

            if (msg.status === 'error') {
              newStatus = 'error';
            } else if (isStatusUpdate && !isCompletionMessage) {
              newStatus = 'thinking';
              setThinkingMessageId(messageId);
            } else {
              newStatus = message.isComplete ? 'complete' : 'streaming';
              // Clear thinking indicator as soon as content starts arriving OR when message is complete
              if ((message.content || message.isComplete) && thinkingMessageId === messageId) {
                setThinkingMessageId(null);
                setToolCallCount(0);
                setCurrentToolCalls([]);
              }
            }

            const updatedContent = msg.content + (message.content || '');
            let isMarkdown = msg.isMarkdown || containsMarkdown(updatedContent);

            if (updatedContent.includes('##') || updatedContent.includes('---') ||
                updatedContent.includes('|')) {
              isMarkdown = true;
            }

            return {
              ...msg,
              content: updatedContent,
              status: newStatus,
              isMarkdown: isMarkdown
            };
          }
          return msg;
        });
      });
    }, [thinkingMessageId])
  });

  const scrollToBottom = useCallback(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, []);

  useEffect(() => {
    scrollToBottom();
  }, [messages, scrollToBottom]);

  // Bot event handlers for copilot
  useBotEvents({
    onProcessingUpdate: useCallback((event: ProcessingUpdateEvent) => {
      // Update message with progress information
      setMessages(prev => {
        const msgIndex = prev.findIndex(msg =>
          msg.role === 'assistant' && msg.status !== 'complete' && msg.status !== 'error'
        );
        if (msgIndex === -1) return prev;

        return prev.map((msg, idx) => {
          if (idx === msgIndex) {
            return {
              ...msg,
              progress: {
                stage: event.stage,
                percent: event.progress
              }
            };
          }
          return msg;
        });
      });
    }, []),

    onToolExecution: useCallback((event: ToolExecutionEvent) => {
      // Update message with tool execution information
      setMessages(prev => {
        const msgIndex = prev.findIndex(msg =>
          msg.role === 'assistant' && msg.status !== 'complete' && msg.status !== 'error'
        );
        if (msgIndex === -1) return prev;

        return prev.map((msg, idx) => {
          if (idx === msgIndex) {
            const toolExecutions = msg.toolExecutions || [];
            const existingToolIndex = toolExecutions.findIndex(t => t.name === event.toolName);

            if (existingToolIndex >= 0) {
              // Update existing tool execution
              toolExecutions[existingToolIndex] = {
                name: event.toolName,
                status: event.status,
                error: event.error
              };
            } else {
              // Add new tool execution
              toolExecutions.push({
                name: event.toolName,
                status: event.status,
                error: event.error
              });
            }

            return {
              ...msg,
              toolExecutions
            };
          }
          return msg;
        });
      });
    }, []),

    onError: useCallback((event: BotErrorEvent) => {
      // Store error for reference
      setErrors(prev => new Map(prev).set(event.errorId, event));

      // Log technical errors to console
      if (!event.isUserFacing) {
        console.error('Copilot technical error:', event);
      }

      // Update the message to show error status
      setMessages(prev => {
        let lastAssistantIndex = -1;
        for (let i = prev.length - 1; i >= 0; i--) {
          if (prev[i].role === 'assistant') {
            lastAssistantIndex = i;
            break;
          }
        }

        if (lastAssistantIndex === -1) return prev;

        return prev.map((msg, idx) => {
          if (idx === lastAssistantIndex) {
            return {
              ...msg,
              status: 'error' as const,
              error: event.isUserFacing ? {
                message: event.errorMessage,
                code: event.errorCode,
                isUserFacing: true
              } : {
                message: 'An error occurred while processing your request',
                code: event.errorCode,
                isUserFacing: false
              }
            };
          }
          return msg;
        });
      });
    }, []),

    onLongRunningOperation: useCallback((event: LongRunningOperationEvent) => {
      // Update message with long running operation status
      setMessages(prev => {
        const msgIndex = prev.findIndex(msg =>
          msg.role === 'assistant' && msg.status !== 'complete' && msg.status !== 'error'
        );
        if (msgIndex === -1) return prev;

        return prev.map((msg, idx) => {
          if (idx === msgIndex) {
            return {
              ...msg,
              progress: {
                stage: event.statusMessage || 'Processing',
                percent: event.progress * 100
              }
            };
          }
          return msg;
        });
      });
    }, [])
  });

  const handleStartSession = useCallback(async () => {
    if (isProcessing || !isRegistered) return;

    const userName = auth?.user?.name || 'User';
    const pageName = window.location.pathname.split('/').filter(Boolean).pop() || 'Home';
    const initMessage = `Hello, my name is ${userName}. I am on the ${pageName} page and I would like to start a session with you.`;

    try {
      await sendBotMessage(initMessage, { isHiddenInit: true });
    } catch (err) {
      console.error('Failed to start session:', err);
      setMessages(prev => [...prev, {
        id: generateMessageId(),
        role: 'assistant',
        content: 'Sorry, something went wrong. Please try again.',
        timestamp: new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }),
        status: 'error'
      }]);
    }
  }, [auth, isProcessing, isRegistered, sendBotMessage, generateMessageId]);

  const handleSubmit = useCallback(async (e: React.FormEvent) => {
    e.preventDefault();
    const currentInput = input.trim();
    if (!currentInput || isProcessing || !isRegistered) return;

    const userMessage: Message = {
      id: generateMessageId(),
      role: 'user',
      content: currentInput,
      timestamp: new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
    };

    setMessages(prev => [...prev, userMessage]);
    setInput('');
    setAutoSubmit(false);

    try {
      await sendBotMessage(currentInput);
    } catch (err) {
      console.error('Failed to send message:', err);
      setMessages(prev => [...prev, {
        id: generateMessageId(),
        role: 'assistant',
        content: 'Sorry, something went wrong. Please try again.',
        timestamp: new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }),
        status: 'error'
      }]);
    }
  }, [input, isProcessing, isRegistered, generateMessageId, sendBotMessage]);

  // Effect to handle auto-submit when input changes
  useEffect(() => {
    if (autoSubmit && input && !isProcessing && isRegistered) {
      const syntheticEvent = {
        preventDefault: () => {},
        target: formRef.current,
        currentTarget: formRef.current,
        bubbles: true,
        cancelable: true,
        defaultPrevented: false,
        isDefaultPrevented: () => false,
        isPropagationStopped: () => false,
        persist: () => {},
        nativeEvent: new Event('submit'),
        stopPropagation: () => {},
      } as unknown as React.FormEvent;
      handleSubmit(syntheticEvent);
    }
  }, [input, autoSubmit, handleSubmit, isProcessing, isRegistered, formRef]);

  // Listen for custom event to set input
  useEffect(() => {
    const handleSetInput = (e: CustomEvent<{ text: string }>) => {
      setInput(e.detail.text);
      setAutoSubmit(true);
    };

    window.addEventListener('set-copilot-input', handleSetInput as EventListener);
    return () => {
      window.removeEventListener('set-copilot-input', handleSetInput as EventListener);
    };
  }, []);

  // Listen for action responses (test results, etc.)
  useEffect(() => {
    if (!connection || !registerMessageHandler || !unregisterMessageHandler) return;

    const handleActionResponse = (message: BotMessage) => {
      console.log('[Copilot] 📬 Action response received:', message);
      console.log('[Copilot] Current sessionId:', sessionId);
      console.log('[Copilot] Message sessionId:', message.sessionId);

      // Only handle messages for this session
      if (message.sessionId !== sessionId) {
        console.log('[Copilot] ⚠️ Session mismatch, ignoring action response');
        return;
      }

      // Add action result to messages
      if (message.content) {
        console.log('[Copilot] ✅ Adding action result to messages');
        const messageContent = message.content;
        const isMarkdown = containsMarkdown(messageContent);
        const isActionResult = message.metadata?.actionType === 'result';

        setMessages(prev => {
          const newMessage = {
            id: message.requestId,
            role: 'assistant' as const,
            content: messageContent,
            status: 'complete' as const,
            isMarkdown,
            isActionResult, // Flag to style specially
            metadata: message.metadata
          };
          console.log('[Copilot] New message:', newMessage);
          console.log('[Copilot] Previous messages count:', prev.length);
          const updatedMessages = [...prev, newMessage];
          console.log('[Copilot] Updated messages count:', updatedMessages.length);
          console.log('[Copilot] Last 2 messages:', updatedMessages.slice(-2));
          return updatedMessages;
        });
      }

      // Clear executing action state
      if (message.metadata?.actionType === 'result') {
        const actionId = message.requestId;
        setExecutingActions(prev => {
          const next = new Set(prev);
          next.delete(actionId);
          return next;
        });
      }
    };

    registerMessageHandler('ReceiveActionResponse', handleActionResponse);

    return () => {
      unregisterMessageHandler('ReceiveActionResponse', handleActionResponse);
    };
  }, [connection, sessionId, registerMessageHandler, unregisterMessageHandler]);

  // Debug: Log when messages change
  useEffect(() => {
    console.log('[Copilot] 📝 Messages array updated, count:', messages.length);
    if (messages.length > 0) {
      console.log('[Copilot] Last message:', messages[messages.length - 1]);
    }
  }, [messages]);

  // Set initial width on mount
  useEffect(() => {
    document.documentElement.style.setProperty('--copilot-width', isVisible ? `${width.get()}px` : '0px');
  }, [width, isVisible]);

  // Update CSS variable when width changes
  useMotionValueEvent(width, "change", (latest) => {
    if (isVisible) {
      document.documentElement.style.setProperty('--copilot-width', `${latest}px`);
    }
  });

  return (
    <AnimatePresence>
      {isVisible && (
        <motion.div
          className="fixed right-0 top-[64px] bottom-0 z-50"
          initial={{ x: "100%" }}
          animate={{ x: 0 }}
          exit={{ x: "100%" }}
          transition={{ type: "spring", bounce: 0, duration: 0.3 }}
        >
          <ResizablePanel
            defaultSize={400}
            minSize={300}
            maxSize={800}
            side="left"
            className="h-full bg-background border-l border-border"
            motionValue={width}
          >
            <div className="flex flex-col h-full">
              <div className="border-b border-border p-3">
                <div className="flex items-center justify-between gap-2">
                  <div className="flex items-center gap-2">
                    <Sparkles className="h-4 w-4 text-primary" />
                    <span className="font-medium">AI Assistant</span>
                  </div>
                  {!isConnected && <span className="text-xs text-muted-foreground">Connecting...</span>}
                  {isConnected && !isRegistered && <span className="text-xs text-muted-foreground">Initializing...</span>}
                  {availableBots.length > 1 && isRegistered && currentBot && (
                    <>
                      {!isBotSelectorOpen ? (
                        <span
                          className="text-xs cursor-pointer hover:underline text-foreground font-medium px-2 py-1"
                          onClick={() => !isProcessing && setIsBotSelectorOpen(true)}
                        >
                          {currentBot.botName}
                        </span>
                      ) : (
                        <select
                          value={currentBot.botId}
                          onChange={(e) => {
                            switchBot(e.target.value);
                            setMessages([]);
                            setIsBotSelectorOpen(false);
                          }}
                          onBlur={() => setIsBotSelectorOpen(false)}
                          autoFocus
                          className="text-xs border border-border rounded px-2 py-1 bg-background text-foreground"
                          disabled={isProcessing}
                        >
                          {availableBots.map((bot) => (
                            <option key={bot.botId} value={bot.botId}>
                              {bot.botName}
                            </option>
                          ))}
                        </select>
                      )}
                    </>
                  )}
                </div>
              </div>
              <div className="flex-1 overflow-y-auto p-4 space-y-2">
                {copilotMode === 'chat' && (
                  <>
                  {messages
                    .filter(msg => {
                      // Filter out hidden initialization messages
                      if (msg.metadata?.isHiddenInit) return false;
                      return (msg.content && msg.content.trim().length > 0) || thinkingMessageId === msg.id;
                    })
                    .length === 0 && !isProcessing && isRegistered && (
                      <div className="text-center text-muted-foreground py-8">
                        <Bot className="w-8 h-8 mx-auto mb-2 opacity-50" />
                        <p className="mb-4">Start a conversation with the AI assistant</p>
                        <Button onClick={handleStartSession} variant="outline" size="sm" className="gap-2">
                          <Play className="h-4 w-4" />
                          Start Session
                        </Button>
                      </div>
                    )}
                  {messages
                    .filter(msg => {
                      // Filter out hidden initialization messages
                      if (msg.metadata?.isHiddenInit) return false;
                      return (msg.content && msg.content.trim().length > 0) || thinkingMessageId === msg.id;
                    })
                    .map((msg) => {
                    const isUser = msg.role === 'user';
                    return (
                      <div
                        key={msg.id}
                        className="copilot-message"
                        style={{
                          display: 'flex',
                          justifyContent: isUser ? 'flex-end' : 'flex-start',
                          marginBottom: 4,
                        }}
                      >
                        <div
                          style={{
                            background: msg.isActionResult
                              ? 'rgba(34, 197, 94, 0.08)' // Green tint for action results
                              : isUser ? 'rgba(0, 120, 255, 0.05)' : 'transparent',
                            color: isUser ? '#0056b3' : '#222',
                            fontWeight: isUser ? 400 : 400,
                            borderRadius: msg.isActionResult ? 8 : 4,
                            padding: msg.isActionResult
                              ? '12px 16px' // More padding for action results
                              : isUser ? '4px 12px' : '4px 0',
                            maxWidth: isUser ? '95%' : '85%',
                            width: isUser ? 'calc(100% - 20px)' : undefined,
                            marginLeft: isUser ? '20px' : undefined,
                            fontSize: '0.95em',
                            border: msg.isActionResult
                              ? '1px solid rgba(34, 197, 94, 0.3)' // Green border for action results
                              : 'none',
                            borderLeft: !isUser && !msg.isActionResult ? '2px solid rgba(128,128,128,0.15)' : undefined,
                            paddingLeft: !isUser && !msg.isActionResult ? '8px' : undefined,
                            textAlign: isUser ? 'right' : 'left',
                            boxShadow: msg.isActionResult
                              ? '0 1px 3px rgba(34, 197, 94, 0.1)' // Subtle shadow for action results
                              : undefined,
                          }}
                        >
                          {msg.status === 'error' && msg.error ? (
                            <Alert variant="destructive" className="border-red-200 bg-red-50 dark:bg-red-900/20 mb-2">
                              <AlertCircle className="h-4 w-4" />
                              <AlertDescription>{msg.error.message}</AlertDescription>
                            </Alert>
                          ) : (
                            <>
                              {msg.isMarkdown ? (
                                <div className="markdown-content text-sm overflow-x-hidden">
                                  <style jsx global>{`
                                    .markdown-content table {
                                      border-collapse: collapse;
                                      width: 100%;
                                      margin: 1rem 0;
                                    }
                                    .markdown-content th, .markdown-content td {
                                      border: 1px solid #e2e8f0;
                                      padding: 0.5rem;
                                    }
                                    .markdown-content th {
                                      background-color: #f8fafc;
                                    }
                                    .dark .markdown-content th {
                                      background-color: #1e293b;
                                      border-color: #475569;
                                    }
                                    .dark .markdown-content td {
                                      border-color: #475569;
                                    }
                                    .markdown-content h2 {
                                      font-size: 1.5rem;
                                      font-weight: 600;
                                      margin-top: 1.5rem;
                                      margin-bottom: 1rem;
                                      border-bottom: 1px solid #e2e8f0;
                                      padding-bottom: 0.5rem;
                                    }
                                    .dark .markdown-content h2 {
                                      border-color: #475569;
                                    }
                                    .markdown-content hr {
                                      margin: 1.5rem 0;
                                      border: 0;
                                      border-top: 1px solid #e2e8f0;
                                    }
                                    .dark .markdown-content hr {
                                      border-color: #475569;
                                    }
                                    .markdown-content .overflow-x-auto {
                                      display: block;
                                      width: 100%;
                                      overflow-x: auto;
                                      -webkit-overflow-scrolling: touch;
                                    }
                                    .markdown-content .overflow-x-auto::-webkit-scrollbar {
                                      height: 8px;
                                    }
                                    .markdown-content .overflow-x-auto::-webkit-scrollbar-track {
                                      background: #f1f1f1;
                                      border-radius: 4px;
                                    }
                                    .markdown-content .overflow-x-auto::-webkit-scrollbar-thumb {
                                      background: #888;
                                      border-radius: 4px;
                                    }
                                    .markdown-content .overflow-x-auto::-webkit-scrollbar-thumb:hover {
                                      background: #555;
                                    }
                                  `}</style>
                                  <ReactMarkdown
                                    remarkPlugins={[remarkGfm]}
                                    components={{
                                      table: ({node: _node, ...props}) => (
                                        <div className="overflow-x-auto my-4 -mx-2 px-2" style={{ WebkitOverflowScrolling: 'touch' }}>
                                          <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700 border border-gray-200 dark:border-gray-700" {...props} />
                                        </div>
                                      ),
                                      thead: ({node: _node, ...props}) => (
                                        <thead className="bg-gray-50 dark:bg-gray-800" {...props} />
                                      ),
                                      tbody: ({node: _node, ...props}) => (
                                        <tbody className="divide-y divide-gray-200 dark:divide-gray-700" {...props} />
                                      ),
                                      th: ({node: _node, ...props}) => (
                                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider" {...props} />
                                      ),
                                      td: ({node: _node, ...props}) => (
                                        <td className="px-4 py-3 text-sm" {...props} />
                                      ),
                                      tr: ({node: _node, ...props}) => (
                                        <tr className="hover:bg-gray-50 dark:hover:bg-gray-700" {...props} />
                                      ),
                                      h1: ({node: _node, ...props}) => (
                                        <h1 className="text-2xl font-bold mt-6 mb-4" {...props} />
                                      ),
                                      h2: ({node: _node, ...props}) => (
                                        <h2 className="text-xl font-bold mt-5 mb-3" {...props} />
                                      ),
                                      h3: ({node: _node, ...props}) => (
                                        <h3 className="text-lg font-bold mt-4 mb-2" {...props} />
                                      ),
                                      ul: ({node: _node, ...props}) => (
                                        <ul className="list-disc pl-6 my-4" {...props} />
                                      ),
                                      ol: ({node: _node, ...props}) => (
                                        <ol className="list-decimal pl-6 my-4" {...props} />
                                      ),
                                      li: ({node: _node, ...props}) => (
                                        <li className="my-1" {...props} />
                                      ),
                                      hr: ({node: _node, ...props}) => (
                                        <hr className="my-6 border-t border-gray-200 dark:border-gray-700" {...props} />
                                      ),
                                      p: ({node: _node, ...props}) => (
                                        <p className="my-3" {...props} />
                                      ),
                                      code: ({node: _node, inline, children, ...props}: any) => {
                                        // For action results, format code blocks specially
                                        if (msg.isActionResult && !inline) {
                                          const text = String(children).replace(/\n$/, '');

                                          // Try to parse as JSON first
                                          let isJson = false;
                                          let jsonContent = null;
                                          try {
                                            jsonContent = JSON.parse(text);
                                            isJson = true;
                                          } catch {
                                            // Not JSON, will use comma formatting
                                          }

                                          if (isJson && jsonContent !== null) {
                                            // Pretty print JSON with syntax highlighting
                                            const prettyJson = JSON.stringify(jsonContent, null, 2);
                                            return (
                                              <div className="my-2 p-3 bg-gray-50 dark:bg-gray-800 rounded border border-gray-200 dark:border-gray-700 overflow-x-auto">
                                                <pre className="font-mono" style={{ fontSize: '1.05em', lineHeight: '1.6', margin: 0 }}>
                                                  {prettyJson}
                                                </pre>
                                              </div>
                                            );
                                          }

                                          // Not JSON - replace commas and semicolons with line breaks for better readability
                                          // Split by both semicolons and commas to handle different formats
                                          const items = text.split(/[;,]/).filter(item => item.trim().length > 0);
                                          const formatted = items.map((item, idx) => (
                                            <span key={idx}>
                                              {item.trim()}
                                              {idx < items.length - 1 && <br />}
                                            </span>
                                          ));
                                          return (
                                            <div className="my-2 p-3 bg-gray-50 dark:bg-gray-800 rounded border border-gray-200 dark:border-gray-700" style={{ fontSize: '1.05em', lineHeight: '1.6' }}>
                                              {formatted}
                                            </div>
                                          );
                                        }
                                        // Regular inline code
                                        return inline ? (
                                          <code className="bg-gray-100 dark:bg-gray-800 px-1 py-0.5 rounded text-sm font-mono" {...props}>
                                            {children}
                                          </code>
                                        ) : (
                                          <code className="block bg-gray-100 dark:bg-gray-800 p-2 rounded my-2 overflow-x-auto font-mono text-sm" {...props}>
                                            {children}
                                          </code>
                                        );
                                      },
                                    }}
                                  >
                                    {msg.content}
                                  </ReactMarkdown>
                                </div>
                              ) : (
                                <span className="text-sm whitespace-pre-wrap">{msg.content}</span>
                              )}

                              {/* Progress indicator */}
                              {msg.progress && msg.status !== 'complete' && (
                                <div className="mt-2 space-y-1">
                                  <div className="flex items-center justify-between text-xs text-muted-foreground">
                                    <span>{msg.progress.stage}</span>
                                    {msg.progress.percent !== undefined && (
                                      <span>{Math.round(msg.progress.percent)}%</span>
                                    )}
                                  </div>
                                  {msg.progress.percent !== undefined && (
                                    <Progress value={msg.progress.percent} className="h-1" />
                                  )}
                                </div>
                              )}

                              {/* Tool executions */}
                              {msg.toolExecutions && msg.toolExecutions.length > 0 && (
                                <div className="mt-2 space-y-1">
                                  {msg.toolExecutions.map((tool, idx) => (
                                    <div key={idx} className="flex items-center gap-2 text-xs text-muted-foreground">
                                      <Wrench className="h-3 w-3" />
                                      <span className="font-mono">{tool.name}</span>
                                      <span className={cn(
                                        "ml-auto",
                                        tool.status === 'Completed' && "text-green-600 dark:text-green-400",
                                        tool.status === 'Failed' && "text-red-600 dark:text-red-400",
                                        tool.status === 'InProgress' && "text-blue-600 dark:text-blue-400"
                                      )}>
                                        {tool.status}
                                      </span>
                                    </div>
                                  ))}
                                </div>
                              )}
                            </>
                          )}
                          {thinkingMessageId === msg.id && (
                            <AnimatedDots
                              content={undefined}
                              toolCallCount={toolCallCount}
                              currentToolCalls={currentToolCalls}
                            />
                          )}
                          {/* Thinking duration and error indicator */}
                          {((thinkingDuration && msg.id === lastCompletedThinkingId) || msg.status === 'error') && (
                            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginTop: '4px' }}>
                              {thinkingDuration && msg.id === lastCompletedThinkingId && (
                                <span className="thinking-duration" style={{ color: '#888', fontSize: '0.85em' }}>
                                  Thought for {formatDuration(thinkingDuration)}
                                </span>
                              )}
                              {msg.status === 'error' && (
                                <span style={{ display: 'flex', alignItems: 'center', gap: '4px', color: '#dc2626', fontSize: '0.85em' }}>
                                  <AlertCircle style={{ width: '12px', height: '12px' }} />
                                  <span>Error occurred</span>
                                </span>
                              )}
                            </div>
                          )}
                        </div>
                      </div>
                    );
                  })}
                <div ref={messagesEndRef} />
                  </>
                )}
                {copilotMode === 'feedback' && (
                  <CopilotFeedbackScreen onClose={() => setCopilotMode('chat')} itemName={context?.itemName ?? ''} />
                )}
              </div>

              {/* ActionButtons Section */}
              {copilotMode === 'chat' && actionButtons.length > 0 && (
                <div className="px-4 py-3 border-t bg-blue-50 dark:bg-blue-950/20">
                  <p className="text-xs font-medium text-blue-700 dark:text-blue-300 mb-2">Available Actions</p>
                  <div className="flex flex-wrap gap-2">
                    {actionButtons.map((button) => (
                      <Button
                        key={button.actionId}
                        size="sm"
                        variant={button.variant === 'primary' ? 'default' : 'outline'}
                        disabled={executingActions.has(button.actionId)}
                        onClick={async () => {
                          if (!connection || !sessionId) {
                            console.error('Cannot execute action: no connection or session');
                            return;
                          }

                          console.log('[Copilot] Executing action:', button);
                          setExecutingActions(prev => new Set(prev).add(button.actionId));

                          try {
                            await connection.invoke('ExecuteArtifactAction', {
                              sessionId: sessionId,
                              actionId: button.actionId,
                              actionMetadata: button.actionMetadata
                            });
                            console.log('[Copilot] Action executed successfully');
                          } catch (error) {
                            console.error('[Copilot] Failed to execute action:', error);
                            alert(`Failed to execute action: ${error}`);
                          } finally {
                            setExecutingActions(prev => {
                              const next = new Set(prev);
                              next.delete(button.actionId);
                              return next;
                            });
                          }
                        }}
                      >
                        {executingActions.has(button.actionId) ? 'Running...' : `${button.icon} ${button.label}`}
                      </Button>
                    ))}
                  </div>
                </div>
              )}

              {copilotMode === 'chat' && (
                <form ref={formRef} onSubmit={handleSubmit} className="p-4 border-t border-border">
                  <div className="flex gap-2">
                    <input
                      type="text"
                      value={input}
                      onChange={(e) => setInput(e.target.value)}
                      placeholder={!isRegistered ? "Connecting..." : "Type your message..."}
                      disabled={isProcessing || !isRegistered}
                      className="flex-1 min-w-0 rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
                    />
                    <button
                      type="submit"
                      disabled={isProcessing || !isRegistered || !input.trim()}
                      className="px-4 py-2 bg-primary text-primary-foreground rounded-md hover:bg-primary/90 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      {isProcessing ? 'Sending...' : 'Send'}
                    </button>
                  </div>
                </form>
              )}
            </div>
          </ResizablePanel>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
